/**
 * One-shot browser geolocation for anonymous marketing visitors (PROMPT B).
 *
 * Mirrors CareVerify's getCurrentPosition pattern — does NOT extend usePosition or useLocation.
 */
import { useEffect, useRef, useState } from 'react';
import { findNearestGeoMarketingMatch } from './findNearestGeoMarketingMatch';

export const GEO_SUGGEST_DISMISS_KEY = 'fusionmsi-geo-suggest-dismissed';

function isDismissedThisSession() {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(GEO_SUGGEST_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissGeoSuggestionForSession() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(GEO_SUGGEST_DISMISS_KEY, '1');
  } catch {
    /* no-op */
  }
}

function oneShotPosition() {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.reject(new Error('Geolocation unavailable'));
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 20_000,
      maximumAge: 0,
    });
  });
}

/**
 * @param {{ enabled?: boolean }} [options]
 * @returns {{ suggestion: { label: string, href: string } | null, dismiss: () => void }}
 */
export default function useMarketingLocationSuggestion({ enabled = true } = {}) {
  const [suggestion, setSuggestion] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled || startedRef.current || isDismissedThisSession()) return undefined;
    startedRef.current = true;

    let cancelled = false;

    const run = async () => {
      try {
        const position = await oneShotPosition();
        if (cancelled) return;

        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        const match = findNearestGeoMarketingMatch(coords);
        if (!cancelled && match) {
          setSuggestion({ label: match.label, href: match.href });
        }
      } catch {
        // Fail silent — permission denied, timeout, or unsupported.
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const dismiss = () => {
    dismissGeoSuggestionForSession();
    setSuggestion(null);
  };

  return { suggestion, dismiss };
}
